import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { FieldState } from 'formstate'
import { Trans } from '@lingui/macro'
import { Message } from '../..'
import { Input } from './Input'
import { PASSWORD_REQUIREMENTS, getInvalidPasswordRequirements } from '../../../utils/password'
import { FieldToInputProps, fieldToInputProps } from '../../../utils/form/fieldToInputProps'

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  margin-bottom: 1.5rem;
  .form-control--input {
    margin-bottom: 0;

    input {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`

const ProgressBar = styled.div<{ $value: number; $color: string }>`
  flex: 1;
  width: ${props => props.$value * 100}%;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 4px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  background: ${props => props.$color};

  transition: all 0.2s ease;
`

const ProgressText = styled.div`
  color: white;
  font-size: 12px;
  font-weight: 800;
`

const Requirements = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: 4px;
`

const Requirement = styled.div<{ $isValid: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;

  font-size: 14px;

  ${props =>
    props.$isValid &&
    css`
      opacity: 0.5;

      & > div:first-child {
        opacity: 0.4;
      }
    `}
`
const MID_PASSWORD_VALUE = 0.4

const PasswordCheckInput = (props: { id: string; label: any; passwordField: FieldState<any> }) => {
  const { id, label, passwordField } = props

  const [fulfilledRequirementIds, setFulfilledRequirementIds] = useState<string[]>([])

  const getValidRequirementIds = () => {
    const p = passwordField?.value
    const validRequirementIds: string[] = []
    PASSWORD_REQUIREMENTS.forEach(r => {
      if (r.checkFulfilled(p)) {
        validRequirementIds.push(r.id)
      }
    })
    return validRequirementIds
  }

  useEffect(() => {
    setFulfilledRequirementIds(getValidRequirementIds())
  }, [passwordField?.value])

  //
  // OVERRIDE FIELD-TO-INPUT-PROPS FUNC
  //
  const fieldToInputPropsIntercepted = (): FieldToInputProps => {
    const fieldProps: FieldToInputProps = fieldToInputProps(passwordField, (val: string) =>
      val.trim()
    )

    const validRequirementIds = getValidRequirementIds()

    const isValid = validRequirementIds.length === PASSWORD_REQUIREMENTS.length
    if (passwordField.hasBeenValidated && !isValid) {
      const invalidRequirements = getInvalidPasswordRequirements(passwordField.value)

      passwordField.setError(invalidRequirements.map(r => r.id).join(', '))
    }

    fieldProps.messages = [] // block error messages (these are now replaced by the requirements)
    fieldProps.error = passwordField.hasBeenValidated ? !isValid : false

    return fieldProps
  }

  //
  // PROGRESS BAR
  //
  const getProgressValue = () => {
    if (!passwordField.hasBeenValidated) {
      return -1 // signalizes that no action has been taken yet
    }
    return fulfilledRequirementIds.length / PASSWORD_REQUIREMENTS.length
  }

  const renderProgressBar = () => {
    const _value = getProgressValue()

    if (_value < 0) {
      // no action taken yet
      return null
    }

    const value = Math.max(_value, 1 / PASSWORD_REQUIREMENTS.length) // show at least a minimal progress bar

    const colorWeak = '#ff4e00'
    const colorMid = '#ffbb00'
    const colorStrong = '#28cc22'

    let color = colorWeak
    let text = <Trans>Slabé</Trans>

    if (value > MID_PASSWORD_VALUE) {
      if (value < 1) {
        color = colorMid
        text = <Trans>Stredné</Trans>
      } else {
        color = colorStrong
        text = <Trans>Silné</Trans>
      }
    }

    return (
      <ProgressBar $value={value} $color={color}>
        <ProgressText>{text}</ProgressText>
      </ProgressBar>
    )
  }

  const [, forceRender] = useState({})

  useEffect(() => {
    const disposer: any = passwordField.onDidChange?.(() => {
      forceRender({})
    })

    return () => {
      if (typeof disposer === 'function') disposer()
    }
  }, [passwordField])

  //
  // RENDER
  //
  return (
    <Wrapper>
      <Input
        id={id}
        type="password"
        withPasswordToggler
        label={label}
        isRequired
        autoComplete="new-password"
        {...fieldToInputPropsIntercepted()}
      />

      {renderProgressBar()}

      {passwordField.hasBeenValidated &&
      fulfilledRequirementIds.length !== PASSWORD_REQUIREMENTS.length ? (
        <Message key={1} type="error" icon="message-error" style={{ marginTop: '8px' }}>
          <Trans>Heslo nespĺňa všetky podmienky:</Trans>
        </Message>
      ) : null}

      <Requirements>
        {PASSWORD_REQUIREMENTS.map(r => {
          const isValid = fulfilledRequirementIds.includes(r.id)

          return (
            <Requirement key={r.id} $isValid={isValid}>
              <div style={isValid ? {} : { marginRight: 3 }}> {isValid ? '✔' : '○'}</div>
              <div>{r.renderLabel()}</div>
            </Requirement>
          )
        })}
      </Requirements>
    </Wrapper>
  )
}

export default PasswordCheckInput
